import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { parseMessageFromError } from '../../../api/axios';
import { useGetGatewayCustomerAccount } from '../../../api/paymentsService';
import {
  MAlertTag,
  MBox,
  MButton,
  MCenterModal,
  MFormField,
  MInput,
  MStack,
  MText,
} from '../../../components/Monetize';
import { logger } from '../../../services/logger';
import {
  LinkPaymentGatewayAccountReq,
  LinkPaymentGatewayAccountSchema,
  LinkPaymentGatewayAccountUI,
} from '../../../types';

interface GatewayAccountSearchModalProps {
  onClose: () => void;
  paymentGatewayId: string;
  onCustomerFound: (gatewayCustomerId: string) => void;
}

export const GatewayAccountSearchModal: React.FC<
  GatewayAccountSearchModalProps
> = ({ onClose, paymentGatewayId, onCustomerFound }) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<LinkPaymentGatewayAccountUI>({
    resolver: zodResolver(LinkPaymentGatewayAccountSchema),
    mode: 'onBlur',
  });

  const [gatewayCustomerId, setGatewayCustomerId] = useState<string>('');
  const [getCustomerAccountError, setGetCustomerAccountError] =
    useState<string>('');

  const {
    data: gatewayCustomerData,
    isLoading,
    error: gatewayCustomerDataError,
    isError,
    isSuccess,
  } = useGetGatewayCustomerAccount(paymentGatewayId, gatewayCustomerId, {
    enabled: !!gatewayCustomerId,
  });

  useEffect(() => {
    if (isError && gatewayCustomerDataError) {
      const { detail } = parseMessageFromError(gatewayCustomerDataError);
      setGetCustomerAccountError(detail);
    }
  }, [isError, gatewayCustomerDataError]);

  useEffect(() => {
    if (isSuccess && gatewayCustomerData) {
      onCustomerFound(gatewayCustomerId);
    }
  }, [isSuccess, gatewayCustomerData, gatewayCustomerId, onCustomerFound]);

  const onSearchSubmit = async (formData: LinkPaymentGatewayAccountReq) => {
    setGetCustomerAccountError(''); // Clear previous errors
    setGatewayCustomerId(formData.gatewayAccountId);
  };

  const onSearchError = (error: unknown) => {
    logger.error('Search payment gateway account Error', error);
  };

  return (
    <MCenterModal
      isOpen
      onClose={onClose}
      modalTitle="Search Payment Gateway"
      size="lg"
      closeOnEsc={false}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
          <MButton onClick={onClose} variant="cancel" minW="auto">
            Cancel
          </MButton>

          <MButton
            isDisabled={!isValid}
            isLoading={isLoading}
            variant="primary"
            minW="auto"
            onClick={handleSubmit(onSearchSubmit, onSearchError)}
          >
            Search
          </MButton>
        </MStack>
      )}
    >
      <MBox>
        <MFormField
          error={errors.gatewayAccountId}
          label="Payment Account ID"
          isRequired
          tooltip="Provide the account or customer ID from the payment gateway"
        >
          <Controller
            name="gatewayAccountId"
            control={control}
            defaultValue=""
            render={({ field }) => <MInput {...field} />}
          />
          {!!getCustomerAccountError && (
            <MAlertTag
              type={'error'}
              padding="4"
              alignItems="flex-start"
              mt="4"
            >
              <MBox ml="1">
                <MText color="tGray.acBlack" fontSize="sm">
                  {getCustomerAccountError}
                </MText>
              </MBox>
            </MAlertTag>
          )}
        </MFormField>
      </MBox>
    </MCenterModal>
  );
};
